import { api } from '../../shared/api/client';
import { saveBlob } from '../../shared/lib/download';
import { putWithProgress } from '../../shared/lib/upload';
import type { Snapshot } from '../snapshots/api';
import type {
  ArchiveJob,
  Examination,
  ExaminationComment,
  ExaminationDetail,
  ExaminationFile,
  ExaminationInput,
  Paged,
  ShareLink,
} from './types';

interface CreateUploadResponse {
  snapshot: Snapshot;
  uploadUrl: string;
  uploadMethod: 'PUT';
}

interface StartArchiveResponse {
  jobId: string;
  uploadUrl: string;
  uploadMethod: 'PUT';
}

export async function listPatientExaminations(patientId: string): Promise<Paged<Examination>> {
  const { data } = await api.get<Paged<Examination>>(`/v1/patients/${patientId}/examinations`);
  return data;
}

export async function createExamination(body: ExaminationInput): Promise<Examination> {
  const { data } = await api.post<Examination>('/v1/examinations', body);
  return data;
}

export async function getExamination(id: string): Promise<ExaminationDetail> {
  const { data } = await api.get<ExaminationDetail>(`/v1/examinations/${id}`);
  return data;
}

/** Сырое содержимое текстового вложения (kind=doc) для просмотра в приложении. */
export async function getFileContent(snapshotId: string): Promise<string> {
  const { data } = await api.get<string>(`/v1/snapshots/${snapshotId}/content`, {
    responseType: 'text',
    transformResponse: (v) => v as string,
  });
  return data;
}

/** Ставит задачу построения 2D-проекции («панорамы») по 3d-серии обследования.
 *  Возвращает снимок-заготовку (status=processing); готовый файл появится в
 *  списке по завершении задачи. curve — дуга, подправленная вручную (см.
 *  getPanoramaPreview); без неё дуга определяется автоматически. */
export async function buildPanorama(
  examinationId: string,
  body: { seriesUid?: string; mode?: 'mip' | 'mean'; curve?: [number, number][] } = {},
): Promise<ExaminationFile> {
  const { data } = await api.post<ExaminationFile>(
    `/v1/examinations/${examinationId}/panorama`,
    body,
  );
  return data;
}

export interface PanoramaPreview {
  width: number;
  height: number;
  /** data:image/png;base64,... — top-view скана с авто-детектированной дугой. */
  image: string;
  points: { x: number; y: number }[];
}

/** Авто-детектированная дуга зубного ряда + top-view картинка для ручной
 *  подгонки. Синхронный запрос — декодирование объёма занимает секунды-
 *  десятки секунд, поэтому таймаут увеличен. */
export async function getPanoramaPreview(examinationId: string): Promise<PanoramaPreview> {
  const { data } = await api.get<PanoramaPreview>(
    `/v1/examinations/${examinationId}/panorama/preview`,
    { timeout: 90_000 },
  );
  return data;
}

export async function updateExamination(
  id: string,
  body: Pick<ExaminationInput, 'examinedAt' | 'modality' | 'note'>,
): Promise<Examination> {
  const { data } = await api.patch<Examination>(`/v1/examinations/${id}`, body);
  return data;
}

export async function deleteExamination(id: string): Promise<void> {
  await api.delete(`/v1/examinations/${id}`);
}

function contentTypeOf(file: File): string {
  return file.type || 'application/octet-stream';
}

/** Загрузка одного файла: создать запись → PUT в S3 → complete. */
export async function uploadFile(
  examinationId: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<Snapshot> {
  const contentType = contentTypeOf(file);
  const { data } = await api.post<CreateUploadResponse>(`/v1/examinations/${examinationId}/files`, {
    fileName: file.name,
    contentType,
  });
  await putWithProgress(data.uploadUrl, file, contentType, onProgress);
  const { data: snap } = await api.post<Snapshot>(
    `/v1/examinations/${examinationId}/files/${data.snapshot.id}/complete`,
  );
  return snap;
}

/** Загрузка ZIP-архива: старт → PUT в S3 → complete (распаковка в воркере). */
export async function uploadArchive(
  examinationId: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<ArchiveJob> {
  const { data } = await api.post<StartArchiveResponse>(
    `/v1/examinations/${examinationId}/archive`,
    { fileName: file.name },
  );
  await putWithProgress(data.uploadUrl, file, 'application/zip', onProgress);
  const { data: job } = await api.post<ArchiveJob>(
    `/v1/examinations/${examinationId}/archive/${data.jobId}/complete`,
  );
  return job;
}

export async function getArchiveJob(examinationId: string, jobId: string): Promise<ArchiveJob> {
  const { data } = await api.get<ArchiveJob>(`/v1/examinations/${examinationId}/archive/${jobId}`);
  return data;
}

/** Скачивание всего обследования одним ZIP (имя — Фамилия_И_О_ДДММГГГГ.zip). */
export async function downloadExaminationArchive(id: string): Promise<void> {
  const res = await api.get<Blob>(`/v1/examinations/${id}/download`, {
    responseType: 'blob',
    timeout: 0,
  });
  saveBlob(res, 'examination.zip');
}

/** Комментарии к обследованию (блок «Снимки»). Редактировать/удалять можно только свои. */
export async function listComments(examinationId: string): Promise<ExaminationComment[]> {
  const { data } = await api.get<{ items: ExaminationComment[] }>(
    `/v1/examinations/${examinationId}/comments`,
  );
  return data.items;
}

export async function createComment(
  examinationId: string,
  body: string,
): Promise<ExaminationComment> {
  const { data } = await api.post<ExaminationComment>(
    `/v1/examinations/${examinationId}/comments`,
    { body },
  );
  return data;
}

export async function updateComment(
  examinationId: string,
  commentId: string,
  body: string,
): Promise<ExaminationComment> {
  const { data } = await api.patch<ExaminationComment>(
    `/v1/examinations/${examinationId}/comments/${commentId}`,
    { body },
  );
  return data;
}

export async function deleteComment(examinationId: string, commentId: string): Promise<void> {
  await api.delete(`/v1/examinations/${examinationId}/comments/${commentId}`);
}

/** Отправка обследования по e-mail: получатель получит ссылку на скачивание архива. */
export async function createShareLink(
  examinationId: string,
  body: { email: string; message?: string },
): Promise<ShareLink> {
  const { data } = await api.post<ShareLink>(`/v1/examinations/${examinationId}/share-links`, body);
  return data;
}

export async function listShareLinks(examinationId: string): Promise<ShareLink[]> {
  const { data } = await api.get<{ items: ShareLink[] }>(
    `/v1/examinations/${examinationId}/share-links`,
  );
  return data.items;
}

export async function revokeShareLink(examinationId: string, linkId: string): Promise<void> {
  await api.delete(`/v1/examinations/${examinationId}/share-links/${linkId}`);
}
